/**
 * Centralised model definitions for the Augmented platform.
 * All model lists, provider mappings, and defaults are defined here.
 */

import type { ModelPolicyTransport } from './model-policy.js';

export type ProviderId = 'openrouter' | 'anthropic' | 'openai' | 'google';

export interface ProviderDefinition {
  id: ProviderId;
  label: string;
}

// ---------------------------------------------------------------------------
// Providers
// ---------------------------------------------------------------------------

/**
 * ENG-9485 (AC2) — kept as a hardcoded four-entry list, deliberately.
 *
 * The issue proposed this was a second stale enum "driving every picker"
 * alongside the validator defect. It is not, and the reason is worth writing
 * down so the next reader does not re-open it: ENG-7185 already deleted the
 * hardcoded MODEL lists, and `paid_models` is the single source of truth for
 * which MODELS exist. What remains here is PROVIDER metadata — an id and a
 * display label — used to group and filter that catalog in the pickers. It is
 * the axis a catalog row is bucketed by, not a list of anything selectable.
 *
 * Gateway models specifically do NOT belong here. Under ADR-0074 a customer
 * gateway's models live in the policy's `(role × slot)` map, reached through
 * the policy's own binding — never through this list and never through
 * `paid_models`. Adding a synthetic 'gateway' provider would imply a catalog of
 * gateway models that cannot exist, since the ids are per customer endpoint.
 *
 * ── One real gap, NOT fixed here, so it is not mistaken for settled ─────────
 *
 * `DEFAULT_XAI_MODELS.primary` is `xai/grok-4.5` (ENG-7928) and there is no
 * `xai` entry below, so `deriveProviderFromModel('xai/grok-4.5')` finds no
 * catalog row and falls through to its `'openrouter'` default. The consequence
 * is cosmetic today — the org model picker buckets that value under OpenRouter
 * and shows it as "(current; unavailable)" — because the xai path resolves the
 * real model at /host/refresh and never consults this list. It is a genuine
 * inconsistency and it is left alone on purpose: adding `xai` here would put a
 * provider in every picker for which the catalog holds ZERO selectable rows,
 * which trades a cosmetic mislabel for an empty dropdown. Fixing it properly
 * means deciding whether xai-direct ids belong in `paid_models` at all, which
 * is an ENG-7928 question, not this one.
 */
export const MODEL_PROVIDERS: ProviderDefinition[] = [
  { id: 'openrouter', label: 'OpenRouter' },
  { id: 'anthropic', label: 'Anthropic (Direct)' },
  { id: 'openai', label: 'OpenAI (Direct)' },
  { id: 'google', label: 'Google (Direct)' },
];

// ---------------------------------------------------------------------------
// Available models — the `paid_models` DB catalog is the single source of truth
// ---------------------------------------------------------------------------
//
// ENG-7185: the hardcoded `MODELS` and `OPENROUTER_RECOMMENDED_MODELS` constants
// that used to enumerate the available models per provider were deleted. The
// catalog (`paid_models`, all 4 providers) now drives every picker via the lean
// GET /models/catalog endpoint, and the agent.update_config validators resolve
// against the same catalog (injected as `allowedModels`). What remains here is
// provider metadata + pure id-shape helpers that don't need the model list.

// ---------------------------------------------------------------------------
// Default models per tier (used when agent has no model configured)
// ---------------------------------------------------------------------------

export const DEFAULT_MODELS = {
  primary: 'openrouter/anthropic/claude-opus-4-6',
  secondary: 'openrouter/google/gemini-3.1-flash-lite-preview',
  tertiary: 'openrouter/openai/gpt-5.4-nano',
} as const;

// ---------------------------------------------------------------------------
// OpenRouter-host default primary (ENG-7608)
// ---------------------------------------------------------------------------
//
// Hosts running Claude Code against OpenRouter (claude_auth_mode='openrouter')
// get their own default primary model, distinct from the fleet-wide
// DEFAULT_MODELS above (which still governs Claude-subscription agents). When an
// OpenRouter agent has no explicit primary_model and no org/platform default is
// set, /host/refresh falls back to this. Must be an enabled `paid_models`
// `openrouter/...` id (seeded in migration 20260709000007) or
// isValidModelForFramework will reject it.
export const DEFAULT_OPENROUTER_MODELS = {
  primary: 'openrouter/x-ai/grok-4.5',
} as const;

/**
 * ENG-7608: pick the effective primary model for an OpenRouter host from an
 * ordered candidate list (agent's explicit model → org default → platform
 * default). Only `openrouter/...` ids are eligible (org/platform defaults can
 * hold a bare direct-provider id like `claude-opus-4-6` that the per-agent
 * OpenRouter key can't serve), so the first `openrouter/`-prefixed candidate
 * wins, falling back to the OpenRouter-host default (Grok 4.5) when none
 * qualifies. The returned value keeps the `openrouter/` prefix; callers strip
 * it with deriveModelValue before handing it to Claude Code's ANTHROPIC_MODEL.
 */
export function resolveOpenRouterPrimaryModel(
  candidates: ReadonlyArray<string | null | undefined>,
): string {
  return (
    candidates.find((v): v is string => typeof v === 'string' && v.startsWith('openrouter/')) ??
    DEFAULT_OPENROUTER_MODELS.primary
  );
}

// ---------------------------------------------------------------------------
// xAI-direct host default primary (ENG-7928)
// ---------------------------------------------------------------------------
//
// Hosts running in xAI auth mode (claude_auth_mode='xai') mint and deliver a
// DIRECT xAI key (XAI_API_KEY, ENG-7883) - NOT an OpenRouter key. Their agents
// (opencode) must therefore run an xai-DIRECT model id (`xai/grok-…`), which
// the opencode adapter (toOpencodeModel -> buildProvider) wires to the `xai`
// provider + {env:XAI_API_KEY}. If such a host instead carries an
// `openrouter/x-ai/…` model (the OpenRouter default), opencode routes it to the
// keyless `openrouter` provider and silently falls back to broken free models
// that 401 on every turn - the ENG-7928 outage. Grok 4.5 mirrors the OpenRouter
// default's underlying model.
export const DEFAULT_XAI_MODELS = {
  primary: 'xai/grok-4.5',
} as const;

/**
 * Coerce a Grok model candidate to its xai-DIRECT (`xai/<model>`) form, or null
 * if it isn't a Grok id. Handles the three shapes that reach an xai host, but in
 * every shape the model suffix must be a Grok id (xAI serves only Grok) - a
 * non-Grok suffix like `xai/not-grok` or `openrouter/x-ai/some-other` returns
 * null so resolveXaiPrimaryModel falls back to the known-good direct default
 * rather than pushing a bogus id onto the xAI key:
 *   - already-direct `xai/grok-4.5`                     → `xai/grok-4.5`
 *   - OpenRouter-namespaced `openrouter/x-ai/grok-4.5`  → `xai/grok-4.5` (self-heal)
 *   - bare `grok-4.5`                                   → `xai/grok-4.5`
 * The OpenRouter case is the self-heal: an xai host whose stored model is still
 * the OpenRouter default is transparently re-pointed at the direct provider it
 * actually has a key for.
 */
export function toXaiDirectModel(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  if (!s) return null;
  let suffix: string | null = null;
  if (s.startsWith('xai/')) {
    suffix = s.slice('xai/'.length);
  } else {
    const orMatch = s.match(/^openrouter\/x-ai\/(.+)$/);
    if (orMatch) suffix = orMatch[1] ?? null;
    else if (/^grok/i.test(s)) suffix = s;
  }
  // xAI serves only Grok - reject any non-Grok suffix so we fall back to default.
  if (!suffix || !/^grok/i.test(suffix)) return null;
  return `xai/${suffix}`;
}

/**
 * ENG-7928: pick the effective xai-direct primary model for an xAI host from an
 * ordered candidate list (agent's explicit model → org default → platform
 * default), each coerced to its `xai/…` direct form. Falls back to the
 * xAI-host default (Grok 4.5) when no candidate is a Grok id. Mirrors
 * resolveOpenRouterPrimaryModel but targets the direct-xAI provider.
 */
export function resolveXaiPrimaryModel(
  candidates: ReadonlyArray<string | null | undefined>,
): string {
  for (const c of candidates) {
    const direct = toXaiDirectModel(c);
    if (direct) return direct;
  }
  return DEFAULT_XAI_MODELS.primary;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build the stored value from provider + model (e.g. "openrouter" + "anthropic/claude-opus-4-6" → "openrouter/anthropic/claude-opus-4-6") */
export function buildStoredModelValue(provider: ProviderId, modelValue: string): string {
  if (provider === 'openrouter') return `openrouter/${modelValue}`;
  return modelValue;
}

/**
 * Extract the provider from a stored model value.
 *
 * OpenRouter ids always carry the `openrouter/` prefix, so they resolve from
 * the string alone. Direct-provider ids are bare (`gpt-4.1`, `claude-opus-4-6`)
 * and can't be classified by shape, so a caller that has loaded the catalog
 * passes it as `catalog` and the row's `provider` wins. With no catalog (or an
 * unknown id) we fall back to `'openrouter'`, matching the prior default.
 */
export function deriveProviderFromModel(
  storedValue: string,
  catalog?: ReadonlyArray<{ value: string; provider: ProviderId }>,
): ProviderId {
  if (storedValue.startsWith('openrouter/')) return 'openrouter';
  const row = catalog?.find((m) => m.value === storedValue);
  if (row) return row.provider;
  return 'openrouter';
}

/** Extract the model-specific part from a stored value */
export function deriveModelValue(storedValue: string, provider: ProviderId): string {
  if (provider === 'openrouter' && storedValue.startsWith('openrouter/')) {
    return storedValue.slice('openrouter/'.length);
  }
  return storedValue;
}

// ---------------------------------------------------------------------------
// Claude Code (subscription) model aliases — ENG-5631
// ---------------------------------------------------------------------------
//
// Claude Code agents run on the operator's Claude subscription, so the model
// is chosen by *family alias* (`fable`/`opus`/`sonnet`/`haiku`) rather than a
// dated SKU or a routed provider/model pair. The launcher passes the alias to
// `claude --model <alias>` (apps/cli/src/lib/persistent-session.ts) — the only
// mechanism that actually takes effect for subscription agents. Using the
// alias (not a dated name like `claude-opus-4-7`) tracks the recommended
// version per family and won't fall through when a dated model is retired.
// `fable` (Fable 5, `claude-fable-5`) is the top tier above Opus.
//
// Deliberately NOT modelled as an entry in MODEL_PROVIDERS/MODELS: those drive
// the org/platform tier-default pickers too, where a bare alias would be an
// invalid value for OpenClaw/NemoClaw agents that route via OpenRouter and
// need a full model id. The alias picker is scoped to claude-code agents in
// the edit-agent UI instead.

export type ClaudeModelAlias = 'fable' | 'opus' | 'sonnet' | 'haiku';

/**
 * Picker option for the edit-agent model dropdown. The bracket variant
 * `opus[fast]` is an Opus family member that additionally opts the session
 * into Anthropic's fast-output mode (ENG-5770) via `/fast` after boot — the
 * launcher still passes bare `opus` to `--model` because `[fast]` is not a
 * model name, it's a runtime mode the manager toggles via slash command.
 */
export type ClaudeModelOption = 'fable' | 'opus' | 'opus[fast]' | 'sonnet' | 'haiku';

/** Alias options shown in the edit-agent model picker for claude-code agents. */
export const CLAUDE_CODE_MODEL_OPTIONS: ReadonlyArray<{ value: ClaudeModelOption; label: string }> = [
  // Fable is the top tier (above Opus); it has no `[fast]` variant — fast-output
  // mode (`/fast`) is currently only valid on Opus 4.6/4.7/4.8.
  { value: 'fable', label: 'Fable' },
  { value: 'opus', label: 'Opus' },
  { value: 'opus[fast]', label: 'Opus (Fast)' },
  { value: 'sonnet', label: 'Sonnet' },
  { value: 'haiku', label: 'Haiku' },
];

// ---------------------------------------------------------------------------
// Paid models catalog + token markup (ENG-7163, ENG-7185)
// ---------------------------------------------------------------------------
//
// The `paid_models` table is the single source of truth for the offered model
// catalog across all 4 providers (ENG-7185 deleted the hardcoded MODELS /
// OPENROUTER_RECOMMENDED_MODELS constants it superseded). A row carries the
// picker metadata; per-MTok cost is joined from model_token_costs, and the
// price shown to a customer applies a markup that layers per-model -> per-org
// -> platform -> 1.0. The small-fast companion lives on the row
// (paid_models.small_fast_model) and is resolved at /host/refresh.

/** A row of the `paid_models` catalog (DB shape, camelCased for callers). */
export interface PaidModel {
  /** Full stored model id (PK); joins model_token_costs.model. */
  model: string;
  provider: ProviderId;
  displayName: string;
  /** Offered to customers in the picker. */
  enabled: boolean;
  /** Ascending display order. */
  sortOrder: number;
  /** Curated cheaper companion (ANTHROPIC_SMALL_FAST_MODEL); null = reuse primary. */
  smallFastModel: string | null;
  /** Per-model markup override (multiplier); null defers to org/platform/1.0. */
  markup: number | null;
}

/** Neutral markup multiplier — raw cost, no markup. The safe default. */
export const DEFAULT_TOKEN_MARKUP = 1.0;

/**
 * Resolve the effective token-cost markup multiplier by layering the three
 * override grains, most-specific first: per-model -> per-org -> platform
 * default -> 1.0 (raw cost). Each input is optional/nullable; a value only wins
 * if it's a finite, non-negative number, so a NULL column, a missing org
 * setting, or a malformed value all fall through to the next grain rather than
 * producing a NaN price. 1.0 is the safe floor: an unconfigured platform shows
 * raw cost, never an accidental markup.
 */
export function resolveTokenMarkup(
  perModel?: number | null,
  orgMarkup?: number | null,
  platformMarkup?: number | null,
): number {
  for (const candidate of [perModel, orgMarkup, platformMarkup]) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0) {
      return candidate;
    }
  }
  return DEFAULT_TOKEN_MARKUP;
}

/**
 * Reduce a platform model identifier to its Claude Code family alias, or
 * `null` when the input is empty or doesn't name a known Claude family. Used
 * by the launcher to build the `--model <alias>` flag and by the edit-agent
 * UI to display a claude-code agent's currently-stored model (which may be a
 * legacy full name like `claude-sonnet-4-6`) as its alias.
 *
 * Handles: dated full names (`claude-opus-4-7`), context-window variants
 * (`claude-opus-4-7[1m]` → bare alias; the auth tier decides 1M availability),
 * fast-mode marker (`opus[fast]` → bare `opus`; /fast is sent at boot by the
 * manager, not via `--model`), legacy `openrouter/anthropic/` routing
 * prefixes, and bare aliases.
 */
export function claudeModelAlias(primaryModel?: string | null): ClaudeModelAlias | null {
  if (!primaryModel) return null;

  // Normalise: lower-case, then drop any provider routing prefix
  // (`openrouter/anthropic/claude-sonnet-4-6` → `claude-sonnet-4-6`).
  const name = (primaryModel.split('/').pop() ?? '').trim().toLowerCase();
  if (!name) return null;

  // Match on the family word so version suffixes (`-5`, `-4-6`) and context-window
  // markers (`[1m]`) are ignored, and a bare alias (`sonnet`) still resolves.
  // The families are mutually exclusive.
  if (name.includes('fable')) return 'fable';
  if (name.includes('opus')) return 'opus';
  if (name.includes('sonnet')) return 'sonnet';
  if (name.includes('haiku')) return 'haiku';

  return null;
}

/**
 * Resolve a stored `primary_model` to its picker option — preserves the
 * `opus[fast]` distinction the dropdown needs. The plain alias resolver
 * collapses both `opus` and `opus[fast]` to `opus`, so the edit-agent UI
 * needs this richer form to show the operator's actual selection.
 */
export function claudeModelOption(primaryModel?: string | null): ClaudeModelOption | null {
  const alias = claudeModelAlias(primaryModel);
  if (!alias) return null;
  if (alias === 'opus' && isClaudeFastMode(primaryModel)) return 'opus[fast]';
  return alias;
}

/**
 * True when the stored `primary_model` carries the `[fast]` marker. Used by
 * the manager to decide whether to send `/fast` after the Claude Code ready
 * banner. The marker is a generic suffix — gated to Opus at the picker layer
 * (Sonnet/Haiku don't currently support /fast), and re-checked at send time
 * against the live banner to avoid sending after a silent model downgrade.
 */
export function isClaudeFastMode(primaryModel?: string | null): boolean {
  if (!primaryModel) return false;
  return /\[fast\]/i.test(primaryModel);
}

// ---------------------------------------------------------------------------
// Claude Code model-id recognition (ENG-9485)
// ---------------------------------------------------------------------------

/**
 * How Claude Code is likely to read a model id handed to it as
 * `ANTHROPIC_MODEL`.
 *
 *  - `alias`        — one of the family aliases the launcher passes to
 *                     `--model` (`opus`, `sonnet`, …). Always understood.
 *  - `canonical`    — an Anthropic-canonical id (`claude-sonnet-4-6`,
 *                     `claude-haiku-4-5-20251001`).
 *  - `unrecognised` — anything else, INCLUDING an Anthropic id carrying a
 *                     vendor namespace (`anthropic-main/claude-sonnet-4-6`).
 */
export type ClaudeModelIdRecognition = 'alias' | 'canonical' | 'unrecognised';

/**
 * ENG-9485. Claude Code infers a model's capabilities by string-matching its
 * id, and an id it does not recognise is assumed to have a **200k context
 * window**. That assumption is silent: nothing errors, and the symptom arrives
 * much later as long conversations compacting or truncating earlier than they
 * should.
 *
 * ── This deliberately does NOT reuse claudeModelAlias ────────────────────────
 *
 * `claudeModelAlias` drops the provider prefix before matching, so it resolves
 * `anthropic-main/claude-sonnet-4-6` to `sonnet` quite happily. Claude Code
 * does NOT — that exact shape is what produced `[claude-code:unrecognized_model]`
 * in testing against a gateway, and it is the shape a customer gateway hands
 * out. Using our own resolver as a proxy for Claude Code's would therefore
 * return "recognised" for the single most likely failing case, which is worse
 * than not checking: it would certify the thing it exists to warn about.
 *
 * So this matches the FULL value, with no prefix stripping — a namespace is
 * precisely the risk, not noise to be normalised away.
 *
 * ── It is a heuristic, and the caller must treat it as one ──────────────────
 *
 * We do not own Claude Code's matcher and cannot enumerate it. `unrecognised`
 * means "we cannot show this is understood", NOT "this is broken" — so the only
 * honest use is a non-blocking caution. Never gate a write on it.
 */
export function classifyClaudeModelId(value: string | null | undefined): ClaudeModelIdRecognition {
  const raw = (value ?? '').trim();
  if (!raw) return 'unrecognised';

  // `[fast]` is our own marker, stripped before the id reaches Claude Code.
  const bare = raw.replace(/\[fast\]/gi, '').trim().toLowerCase();
  if (!bare) return 'unrecognised';

  if (CLAUDE_CODE_MODEL_OPTIONS.some((o) => o.value.replace(/\[fast\]/gi, '') === bare)) {
    return 'alias';
  }

  // Anthropic-canonical: `claude-<family>-<version…>`, anchored at the START so
  // a namespaced or vendor-rewritten id does not qualify.
  if (/^claude-(fable|opus|sonnet|haiku)-\d/.test(bare)) return 'canonical';

  return 'unrecognised';
}

/**
 * A one-sentence caution for an id Claude Code may not recognise, or null when
 * there is nothing to say. Returns prose rather than a boolean so the warning
 * reads the same wherever it is surfaced.
 *
 * Only meaningful for `claude-code` agents: it is Claude Code's matcher that
 * makes the assumption, and no other framework consumes `ANTHROPIC_MODEL`.
 */
export function claudeModelIdContextWarning(
  value: string | null | undefined,
  framework: string | null | undefined,
): string | null {
  if (framework !== 'claude-code') return null;
  if (classifyClaudeModelId(value) !== 'unrecognised') return null;
  return (
    'Claude Code may not recognise this model id and would assume a 200k context ' +
    'window. Long conversations can then compact or truncate earlier than expected.'
  );
}

// ---------------------------------------------------------------------------
// Model-value validation (ENG-6425)
// ---------------------------------------------------------------------------
//
// Used by the admin-debug `agent.update_config` write path to reject an invalid
// model before it is persisted (route, request-time) and again before it is
// applied (broker, execution-time). The legal value space depends on the
// agent's framework: claude-code (subscription) agents take a bare family alias
// (`CLAUDE_CODE_MODEL_OPTIONS`), every other framework takes a full stored model
// id from the `paid_models` catalog (openrouter values carry the `openrouter/`
// prefix; the direct providers are bare). Putting an openrouter id on a
// subscription agent — or a bare alias on an openrouter agent — is exactly the
// misconfiguration these guards exist to prevent.

/** True iff `value` is a valid Claude Code subscription model picker option. */
export function isClaudeCodeModelOption(value: string): value is ClaudeModelOption {
  return CLAUDE_CODE_MODEL_OPTIONS.some((o) => o.value === value);
}

/**
 * ENG-9747 (ADR-0074) — the resolved model policy, as far as validation cares.
 *
 * Two fields, and the second is the one that keeps this honest.
 */
export interface ModelPolicyValidationContext {
  /** The ACTIVE binding's transport — the failed-over role's, once one exists. */
  readonly transport: ModelPolicyTransport;
  /**
   * Can the manager actually honour this binding? Derived by the caller from
   * `resolveModelPolicyActuation`, never restated here.
   *
   * It exists because an unhonoured gateway policy is WORSE than no policy for
   * this decision. While `resolveBindingCredential` returns `blocked` (every
   * gateway binding in the fleet today — ENG-9481 owns the credential shape),
   * the agent runs on its host's own Anthropic auth and its stored alias is
   * what reaches `claude --model`. Accepting a gateway's model id in that state
   * would launch the agent with `--model openai-main/gpt-5` against Anthropic:
   * a value the operator was told was valid, on an agent that then does not
   * start. So the loosening below is gated on the same fact the read-back is.
   */
  readonly honoured: boolean;
}

/**
 * Is `value` shaped like a model id a customer's gateway could serve?
 *
 * A SHAPE check, deliberately, and it can be nothing else: the models behind a
 * gateway are per-endpoint (TrueFoundry's `openai-main/gpt-5`, another
 * customer's `anthropic/claude-…`), so there is no catalog to check against and
 * inventing one would reject the ids this exists to admit.
 *
 * It is a strict SUPERSET of the alias space — every `CLAUDE_CODE_MODEL_OPTIONS`
 * value satisfies it, asserted by a test. That property is the point: the
 * gateway branch must never reject something the branch it replaces accepted,
 * or a policy assignment would silently invalidate an agent's stored model.
 * A future tightening that breaks it reintroduces ADR-0074's line-41 defect
 * ("rejects every gateway model id") one alias at a time.
 */
export function isGatewayModelId(value: string): boolean {
  const s = value.trim();
  if (s.length === 0 || s.length > 200) return false;
  // C0 and C1 controls as well as whitespace (CodeRabbit, PR #5586). `\s` does
  // NOT match NUL, so `openai-main/gpt-5\u0000x` validated — and a NUL is the
  // one character this tree already knows is invisible everywhere it travels:
  // git renders the file binary and ripgrep drops it mid-traversal, which is
  // why `check-source-files-are-text.mjs` exists. A model id is going into a
  // spawn env and a log line, so the same reasoning applies.
  return s === value && !/[\s\u0000-\u001F\u007F-\u009F]/.test(s);
}

/**
 * Validate a proposed model value for an agent of the given framework.
 * `claude-code` → bare family alias, UNLESS the agent's host is in OpenRouter
 * mode (ENG-7152), in which case it takes a stored `openrouter/...` id instead
 * (the host overrides the subscription with a base-URL + per-agent key). Any
 * other framework → full stored model id. Returns false for an unknown value
 * (or one of the wrong shape), so the caller can reject it before the DB.
 *
 * ENG-7185: the legal model set IS the `paid_models` catalog — there is no
 * static fallback any more (MODELS / OPENROUTER_RECOMMENDED_MODELS were deleted).
 * A caller reads the enabled catalog ids and passes them as `opts.allowedModels`
 * (the full set is fine for both routed frameworks and claude-code OpenRouter
 * mode; a bare alias never matches an `openrouter/...` id and vice versa). The
 * list is authoritative when PRESENT (a successful read returning `[]` rejects
 * every model). An OMITTED list (`undefined` - the catalog couldn't be read)
 * fails CLOSED: a model that needs catalog validation is rejected rather than
 * waved through, so a catalog outage can't let an arbitrary model be persisted.
 */
export function isValidModelForFramework(
  framework: string | null | undefined,
  value: string,
  opts?: {
    hostOpenRouter?: boolean;
    allowedModels?: readonly string[];
    /**
     * ENG-9747 — the policy governing this agent, when one does. OMITTED is the
     * pre-policy path and is byte-identical to it: every branch below this line
     * is untouched, so a caller that does not resolve a policy gets exactly the
     * answer it got before.
     */
    policy?: ModelPolicyValidationContext;
  },
): boolean {
  // ENG-9747 — the ADR-0074 defect: "isValidModelForFramework rejects every
  // gateway model id", which is what made a gateway transport one nobody could
  // point at a model.
  //
  // BELOW `hostOpenRouter`, and that placement is the rule rather than a
  // preference. OpenRouter supersedes a policy outright in the manager — gate 2
  // of `resolveModelPolicySpawnEnv`, which returns before it ever consults the
  // binding — so an OpenRouter host with a gateway policy assigned must keep
  // taking `openrouter/…` ids and nothing else.
  //
  // `honoured` is what keeps this from being a loosening: see the field's own
  // note. For every input reachable in the fleet TODAY it is false for a
  // gateway binding, so this branch does not fire and the answer is unchanged.
  // The pin that says when that stops being true lives in
  // `eng-9820-model-policy-actuation.test.ts` — it fails the day a
  // `modelPolicyGatewayToken` producer appears.
  if (!opts?.hostOpenRouter && opts?.policy?.transport === 'gateway' && opts.policy.honoured) {
    return isGatewayModelId(value);
  }
  if (framework === 'claude-code' && !opts?.hostOpenRouter) {
    return isClaudeCodeModelOption(value);
  }
  // Routed framework, or claude-code in OpenRouter mode: the value must be an
  // enabled catalog model. Fail closed when the catalog list is unavailable.
  const allowed = opts?.allowedModels;
  if (allowed === undefined) return false;
  // ENG-7185: a claude-code agent on an OpenRouter host must use a real
  // `openrouter/...` id. The catalog now also holds bare direct-provider ids
  // (gpt-4.1, claude-opus-4-6, …) which the subscription override must never
  // persist on an OpenRouter host, so reject anything without the prefix.
  if (framework === 'claude-code' && opts?.hostOpenRouter && !value.startsWith('openrouter/')) {
    return false;
  }
  return allowed.includes(value);
}
